import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  Archive,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Copy,
  FilePlus2,
  Loader2,
  RefreshCw,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import { agreementStateLabel } from "@shared/agreementPortal";
import AgreementAttachmentLinks from "./AgreementAttachmentLinks";
import SignedAgreementsPanel from "./SignedAgreementsPanel";
const AgreementTemplateEditor = lazy(() => import("./AgreementTemplateEditor"));

type Template = {
  id: string;
  status: "draft" | "active" | "archived";
  version: number;
  configuration: {
    title: string;
    contractingEntity: string;
    requiresCompanySignature: boolean;
    attachments: Array<{ id: string; label: string; required: boolean }>;
  };
};

type AgreementHistory = {
  requests: Array<{
    lifecycle: {
      id: string;
      state: string;
      recipientSignedAt: string | null;
      completedAt: string | null;
    };
    snapshot: {
      configuration: {
        title: string;
        attachments: Array<{ id: string; label: string; required: boolean }>;
      };
      recipient: {
        printedName: string;
        record: { kind: "lead" | "rep"; id: number };
      };
    };
  }>;
  legacyDocuments: Array<{
    id: number;
    leadId: number;
    docType: string;
    label: string;
    fileKey: string;
    status: string;
    uploadedAt: string;
  }>;
};

type RecipientRecord = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

function RecipientSearch({
  records,
  value,
  onSelect,
}: {
  records: RecipientRecord[];
  value: string;
  onSelect: (record: RecipientRecord) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = records.find(record => String(record.id) === value);
  const name = (record: RecipientRecord) =>
    [record.firstName, record.lastName].filter(Boolean).join(" ") ||
    `Record #${record.id}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="mt-1.5 flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left font-normal"
        >
          <span
            className={`min-w-0 flex-1 truncate ${selected ? "text-gray-900" : "text-gray-400"}`}
          >
            {selected
              ? `${name(selected)} - ${selected.email ?? "No email"}`
              : "Search by name, email, or record ID"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput placeholder="Search recipients..." />
          <CommandList>
            <CommandEmpty>No matching recipient found.</CommandEmpty>
            <CommandGroup>
              {records.map(record => (
                <CommandItem
                  key={record.id}
                  value={`${name(record)} ${record.email ?? ""} ${record.id}`}
                  onSelect={() => {
                    onSelect(record);
                    setOpen(false);
                  }}
                  className="items-start py-2"
                >
                  <Check
                    className={`mt-0.5 h-4 w-4 ${String(record.id) === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {name(record)}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {record.email ?? "No email"} - Record #{record.id}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body.error ?? "The agreement request failed.");
  return body;
}

export default function AgreementPortalPanel() {
  const [section, setSection] = useState<"library" | "send" | "history">(
    "library"
  );
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [history, setHistory] = useState<AgreementHistory>({
    requests: [],
    legacyDocuments: [],
  });
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [editingFile, setEditingFile] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [recordKind, setRecordKind] = useState<"lead" | "rep">("lead");
  const [recordId, setRecordId] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [companySignerId, setCompanySignerId] = useState("");
  const [companySignerTitle, setCompanySignerTitle] = useState("");
  const [acknowledgingId, setAcknowledgingId] = useState("");
  const [signingUrl, setSigningUrl] = useState("");
  const [requestId, setRequestId] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState<"sent" | "failed" | "">(
    ""
  );
  const leads = trpc.lead.list.useQuery(
    { includeAll: true },
    { enabled: enabled === true, staleTime: 60_000 }
  );
  const reps = trpc.rep.list.useQuery(undefined, {
    enabled: enabled === true,
    staleTime: 60_000,
  });
  const members = trpc.teamMembers.list.useQuery(undefined, {
    enabled: enabled === true,
    staleTime: 60_000,
  });

  const load = async () => {
    const status = await api<{ enabled: boolean }>("/api/agreements/status");
    setEnabled(status.enabled);
    if (status.enabled) {
      const [nextTemplates, nextHistory] = await Promise.all([
        api<Template[]>("/api/agreements/admin/templates"),
        api<AgreementHistory>("/api/agreements/admin/history"),
      ]);
      setTemplates(nextTemplates);
      setHistory(nextHistory);
    }
  };
  useEffect(() => {
    void load().catch(error => {
      setEnabled(false);
      toast.error(error.message);
    });
  }, []);
  const active = templates.filter(template => template.status === "active");
  const records = useMemo(
    () => (recordKind === "lead" ? (leads.data ?? []) : (reps.data ?? [])),
    [recordKind, leads.data, reps.data]
  );
  const chosen = templates.find(template => template.id === templateId);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      await api("/api/agreements/admin/templates", {
        method: "POST",
        body: data,
      });
      setFile(null);
      await load();
      toast.success("Agreement added as a draft.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };
  const uploadConfigured = async (configuration: any) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    data.append("configuration", JSON.stringify(configuration));
    await api("/api/agreements/admin/templates", {
      method: "POST",
      body: data,
    });
    setEditingFile(false);
    setFile(null);
    await load();
    toast.success("Configured agreement added as a draft.");
  };

  const activate = async (template: Template) => {
    setBusy(true);
    try {
      if (template.configuration.requiresCompanySignature) {
        const member = (members.data ?? []).find(
          (item: any) => item.id === Number(companySignerId)
        );
        if (!member)
          throw new Error("Select the approved company authority first.");
        const title = companySignerTitle.trim();
        if (!title) throw new Error("The authority title is required.");
        await api(`/api/agreements/admin/templates/${template.id}/authority`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            teamMemberId: member.id,
            contractingEntity: template.configuration.contractingEntity,
            printedName: member.name,
            title,
          }),
        });
      }
      await api(`/api/agreements/admin/templates/${template.id}/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ previewApproved: true }),
      });
      await load();
      toast.success("Agreement activated.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Activation failed."
      );
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    setSigningUrl("");
    setRequestId("");
    setDeliveryStatus("");
    try {
      if (!templateId || !recordId)
        throw new Error("Select an agreement and recipient.");
      const result = await api<{
        signingUrl: string;
        request: { lifecycle: { id: string } };
        delivery: { status: "sent" | "failed"; error?: string };
      }>("/api/agreements/admin/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateVersionId: templateId,
          record: { kind: recordKind, id: Number(recordId) },
          recipientEmail,
        }),
      });
      setSigningUrl(result.signingUrl);
      setRequestId(result.request.lifecycle.id);
      setDeliveryStatus(result.delivery.status);
      if (result.delivery.status === "sent")
        toast.success("Secure signing request emailed.");
      else toast.error(result.delivery.error ?? "Email delivery failed.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "The link could not be created."
      );
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async (id: string) => {
    setAcknowledgingId(id);
    try {
      await api(`/api/agreements/admin/requests/${id}/acknowledge`, {
        method: "POST",
      });
      toast.success("Agreement acknowledged. The completed PDF is preparing.");
      await load();
      window.setTimeout(() => void load().catch(() => undefined), 6_000);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "The agreement could not be acknowledged."
      );
    } finally {
      setAcknowledgingId("");
    }
  };

  const resend = async () => {
    if (!requestId) return;
    setBusy(true);
    try {
      const result = await api<{
        signingUrl: string;
        delivery: { status: "sent" };
      }>(`/api/agreements/admin/requests/${requestId}/resend`, {
        method: "POST",
      });
      setSigningUrl(result.signingUrl);
      setDeliveryStatus("sent");
      toast.success("Secure signing request emailed.");
    } catch (error) {
      setDeliveryStatus("failed");
      toast.error(
        error instanceof Error ? error.message : "Email delivery failed."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {editingFile && file && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/70 text-sm text-white">
              Loading PDF editor...
            </div>
          }
        >
          <AgreementTemplateEditor
            file={file}
            onCancel={() => setEditingFile(false)}
            onSave={uploadConfigured}
          />
        </Suspense>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Agreement Portal</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage reusable agreements, send secure links, and retrieve
            completed copies.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1" role="tablist">
          {(
            [
              ["library", "Library"],
              ["send", "Send"],
              ["history", "Signed history"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setSection(key);
                if (key === "history") {
                  void load().catch(error =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : "Agreement history could not be refreshed."
                    )
                  );
                }
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${section === key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {enabled === null && (
        <div className="flex items-center gap-2 py-10 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading agreement workspace...
        </div>
      )}
      {enabled === false && (
        <div className="border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The new agreement workflow is safely disabled until private storage
          and the release flag are configured. Existing signed history remains
          available.
        </div>
      )}
      {section === "history" && (
        <div className="space-y-10">
          <section>
            <h2 className="text-lg font-bold text-gray-900">
              Agreement Portal history
            </h2>
            <p className="mt-0.5 text-sm text-gray-400">
              Requests created through the centralized agreement workflow.
            </p>
            <div className="mt-4 divide-y divide-gray-100 border-y border-gray-100">
              {history.requests.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">
                  No portal requests yet.
                </p>
              ) : (
                history.requests.map(item => (
                  <div
                    key={item.lifecycle.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {item.snapshot.configuration.title}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {item.snapshot.recipient.printedName} ·{" "}
                        {item.snapshot.recipient.record.kind} #
                        {item.snapshot.recipient.record.id}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-500">
                        {agreementStateLabel(item.lifecycle.state)}
                      </span>
                      {item.lifecycle.state === "awaiting_company" && (
                        <button
                          type="button"
                          disabled={acknowledgingId === item.lifecycle.id}
                          onClick={() => void acknowledge(item.lifecycle.id)}
                          className="rounded-lg bg-[#84ab4a] px-3 py-1.5 font-semibold text-white hover:bg-[#73963f] disabled:opacity-50"
                        >
                          {acknowledgingId === item.lifecycle.id
                            ? "Acknowledging..."
                            : "Acknowledge and countersign"}
                        </button>
                      )}
                      {item.lifecycle.state === "completed" && (
                        <a
                          href={`/api/agreements/admin/requests/${item.lifecycle.id}/final`}
                          className="font-semibold text-[#6f923d] underline"
                        >
                          Download
                        </a>
                      )}
                      <AgreementAttachmentLinks
                        requestId={item.lifecycle.id}
                        requirements={
                          item.snapshot.configuration.attachments ?? []
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
          <section>
            <h2 className="text-lg font-bold text-gray-900">
              Existing client agreement documents
            </h2>
            <p className="mt-0.5 text-sm text-gray-400">
              Existing agreement and enrollment files remain connected to their
              original client records.
            </p>
            <div className="mt-4 divide-y divide-gray-100 border-y border-gray-100">
              {history.legacyDocuments.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">
                  No existing client agreement documents found.
                </p>
              ) : (
                history.legacyDocuments.map(document => (
                  <div
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {document.label}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        Client #{document.leadId} · {document.status} ·{" "}
                        {new Date(document.uploadedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <a
                      href={`/api/files/${document.fileKey}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold text-[#6f923d] underline"
                    >
                      View
                    </a>
                  </div>
                ))
              )}
            </div>
          </section>
          <SignedAgreementsPanel />
        </div>
      )}
      {section === "library" && enabled && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 pb-5">
            <label className="flex min-w-[260px] flex-1 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 hover:border-[#84ab4a] hover:bg-[#84ab4a]/5">
              <FilePlus2 className="h-5 w-5 text-[#6f923d]" />
              <span className="truncate">
                {file?.name ?? "Choose an approved PDF"}
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={event => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
            <button
              disabled={!file || busy}
              onClick={upload}
              className="rounded-lg bg-[#84ab4a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#73963f] disabled:opacity-40"
            >
              Use recognized layout
            </button>
            <button
              disabled={!file || busy}
              onClick={() => setEditingFile(true)}
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Configure fields
            </button>
            <button
              onClick={() => void load()}
              className="rounded-lg border border-gray-200 p-2.5 text-gray-500 hover:bg-gray-50"
              aria-label="Refresh agreements"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100 border-y border-gray-100">
            {templates.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-400">
                No reusable agreements yet.
              </p>
            ) : (
              templates.map(template => (
                <div
                  key={template.id}
                  className="flex flex-wrap items-center justify-between gap-4 py-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {template.configuration.title}
                      </span>
                      <span className="text-xs text-gray-400">
                        v{template.version}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${template.status === "active" ? "bg-green-50 text-green-700" : template.status === "draft" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {template.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {template.configuration.contractingEntity}
                      {template.configuration.requiresCompanySignature
                        ? " · Admin acknowledgment applies the approved company signature"
                        : " · Recipient signature only"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`/api/agreements/admin/templates/${template.id}/source`}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                      Preview
                    </a>
                    {template.status === "draft" && (
                      <>
                        {template.configuration.requiresCompanySignature && (
                          <>
                            <select
                              aria-label="Approved company authority"
                              value={companySignerId}
                              onChange={event =>
                                setCompanySignerId(event.target.value)
                              }
                              className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                            >
                              <option value="">
                                Approved company authority
                              </option>
                              {(members.data ?? [])
                                .filter((m: any) => m.status === "active")
                                .map((m: any) => (
                                  <option key={m.id} value={m.id}>
                                    {m.name}
                                  </option>
                                ))}
                            </select>
                            <input
                              aria-label="Approved authority title"
                              value={companySignerTitle}
                              onChange={event =>
                                setCompanySignerTitle(event.target.value)
                              }
                              placeholder="Authority title"
                              className="w-36 rounded-lg border border-gray-200 px-2 py-1.5 text-xs"
                            />
                          </>
                        )}
                        <button
                          disabled={busy}
                          onClick={() => void activate(template)}
                          className="flex items-center gap-1 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Activate
                        </button>
                      </>
                    )}
                    {template.status === "active" && (
                      <button
                        onClick={() =>
                          void api(
                            `/api/agreements/admin/templates/${template.id}/archive`,
                            { method: "POST" }
                          ).then(load)
                        }
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
                        aria-label="Archive agreement"
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {section === "send" && enabled && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700">
              Agreement
              <select
                value={templateId}
                onChange={event => setTemplateId(event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-normal"
              >
                <option value="">Select an active agreement</option>
                {active.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.configuration.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-[130px_1fr]">
              <label className="text-sm font-semibold text-gray-700">
                Record type
                <select
                  value={recordKind}
                  onChange={event => {
                    setRecordKind(event.target.value as any);
                    setRecordId("");
                    setRecipientEmail("");
                  }}
                  className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 font-normal"
                >
                  <option value="lead">Client</option>
                  <option value="rep">Associate</option>
                </select>
              </label>
              <div className="text-sm font-semibold text-gray-700">
                Recipient
                <RecipientSearch
                  records={records as RecipientRecord[]}
                  value={recordId}
                  onSelect={selected => {
                    setRecordId(String(selected.id));
                    setRecipientEmail(selected.email ?? "");
                  }}
                />
              </div>
            </div>
            <label className="block text-sm font-semibold text-gray-700">
              Delivery email
              <input
                type="email"
                value={recipientEmail}
                onChange={event => setRecipientEmail(event.target.value)}
                placeholder="recipient@example.com"
                className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-normal"
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                The secure signing request and completed copy will be sent here.
              </span>
            </label>
            <button
              disabled={busy || !templateId || !recordId || !recipientEmail}
              onClick={send}
              className="flex items-center gap-2 rounded-xl bg-[#84ab4a] px-5 py-3 text-sm font-semibold text-white hover:bg-[#73963f] disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Email secure signing request
            </button>
          </div>
          <aside className="border-l border-gray-100 pl-6">
            <h3 className="text-sm font-semibold text-gray-900">Delivery</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">
              The system emails a request-specific secure link and records
              whether the provider accepted it.
            </p>
            {chosen?.configuration.requiresCompanySignature && (
              <div className="mt-4 rounded-xl bg-[#84ab4a]/10 p-3 text-xs leading-5 text-gray-700">
                After the recipient signs, the request moves to Signed history.
                An admin acknowledges it there, then the approved company
                signature is applied and the completed PDF is prepared.
              </div>
            )}
            {deliveryStatus && (
              <div
                className={`mt-4 rounded-xl p-3 text-xs ${deliveryStatus === "sent" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}`}
              >
                {deliveryStatus === "sent"
                  ? `Email accepted for delivery to ${recipientEmail}.`
                  : "Email delivery failed. The agreement request is preserved and can be retried safely."}
                {deliveryStatus === "failed" && requestId && (
                  <button
                    disabled={busy}
                    onClick={resend}
                    className="mt-3 block font-semibold underline disabled:opacity-40"
                  >
                    Retry email delivery
                  </button>
                )}
              </div>
            )}
            {signingUrl && (
              <div className="mt-4 rounded-xl bg-green-50 p-3">
                <p className="break-all text-xs text-green-900">{signingUrl}</p>
                <button
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(signingUrl)
                      .then(() => toast.success("Link copied."))
                  }
                  className="mt-3 flex items-center gap-1 text-xs font-semibold text-green-800"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy secure link
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
